import { TaskModel } from '@/app/models/tasks/taskModel'
import { TripGenerationMode } from '@/app/constants/delegation/tripGenerationMode'
import {
  findCurrencyTableIssue,
  getCurrencyTableIssueReason,
} from '@/modules/shared/utils/currencyTableIssue'

type Translate = (key: string) => string

type BatchDriverResult = {
  Success?: boolean
  success?: boolean
  Skipped?: boolean
  skipped?: boolean
  RequiresCoDriverDecision?: boolean
  requiresCoDriverDecision?: boolean
  Code?: string
  code?: string
  Message?: string
  message?: string
  Events?: unknown[]
  events?: unknown[]
}

const format = (template: string, ...values: Array<number | string>) =>
  values.reduce<string>(
    (text, value, index) =>
      text.replace(new RegExp(`\\{${index}\\}`, 'g'), String(value)),
    template,
  )

const lowerFirst = (value: string) =>
  value ? value.charAt(0).toLowerCase() + value.slice(1) : value

const getGenerationModeTitle = (
  task: TaskModel,
  translate: Translate,
): string => {
  const generationMode = task.data?.generationMode
  if (typeof generationMode !== 'number') {
    return ''
  }

  const modeKey = TripGenerationMode[generationMode]
  if (!modeKey) {
    return ''
  }

  const titleKey = `groupedRibbon.${lowerFirst(modeKey)}Title`
  const title = translate(titleKey)
  return title && title !== titleKey ? title : modeKey
}

const withGenerationMode = (
  details: string,
  task: TaskModel,
  translate: Translate,
) => {
  const modeTitle = getGenerationModeTitle(task, translate)
  if (!modeTitle) {
    return details
  }

  return `${format(
    translate('businessTrip.generation-result-mode'),
    modeTitle,
  )} ${details}`
}

export const getBusinessTripTaskDetails = (
  task: TaskModel,
  translate: Translate,
): string => {
  if (!task.logs) {
    return withGenerationMode(task.message || '', task, translate)
  }

  try {
    const results = JSON.parse(task.logs) as BatchDriverResult[]
    if (!Array.isArray(results)) {
      return withGenerationMode(task.message || '', task, translate)
    }

    const successfulResults = results.filter(
      (result) => result.Success ?? result.success,
    )
    const skippedCount = results.filter(
      (result) => result.Skipped ?? result.skipped,
    ).length
    const coDriverDecisionCount = results.filter(
      (result) =>
        result.RequiresCoDriverDecision ??
        result.requiresCoDriverDecision ??
        (result.Code ?? result.code) === 'COPY_TO_CODRIVER',
    ).length
    const failedCount =
      results.length -
      successfulResults.length -
      skippedCount -
      coDriverDecisionCount
    const totalDrivers = results.length
    // Cala partia potrafi polec z jednego powodu (brak kursu walut na wspolny dzien
    // rozliczenia). Samo "nie udalo sie dla 32 kierowcow" nie mowilo, co poprawic.
    const currencyIssue = findCurrencyTableIssue(results)
    const failureReason = currencyIssue
      ? ` ${getCurrencyTableIssueReason(currencyIssue, translate)}.`
      : ''
    const successfulDrivers = successfulResults.length
    const generatedEvents = successfulResults.reduce((total, result) => {
      const events = result.Events ?? result.events
      return total + (Array.isArray(events) ? events.length : 0)
    }, 0)
    const generatedTrips = Math.floor(generatedEvents / 2)
    const driverSummary = format(
      translate('businessTrip.generation-result-successful-drivers'),
      successfulDrivers,
      totalDrivers,
    )
    const skippedSummary =
      skippedCount > 0
        ? ` ${format(
            translate('businessTrip.generation-result-skipped'),
            skippedCount,
          )}`
        : ''
    const coDriverSummary =
      coDriverDecisionCount > 0
        ? ` ${format(
            translate('businessTrip.generation-result-codriver-decision'),
            coDriverDecisionCount,
          )}`
        : ''

    if (generatedTrips > 0 && failedCount > 0) {
      const details = format(
        translate('businessTrip.generation-result-generated-failed'),
        generatedTrips,
        failedCount,
      )
      return withGenerationMode(
        `${driverSummary}${skippedSummary}${coDriverSummary} ${details}${failureReason}`,
        task,
        translate,
      )
    }

    if (generatedTrips > 0) {
      const details = format(
        translate('businessTrip.generation-result-generated'),
        generatedTrips,
      )
      return withGenerationMode(
        `${driverSummary}${skippedSummary}${coDriverSummary} ${details}`,
        task,
        translate,
      )
    }

    if (failedCount > 0) {
      const details = format(
        translate('businessTrip.generation-result-failed'),
        failedCount,
      )
      return withGenerationMode(
        `${driverSummary}${skippedSummary}${coDriverSummary} ${details}${failureReason}`,
        task,
        translate,
      )
    }

    if (skippedCount > 0 || coDriverDecisionCount > 0) {
      return withGenerationMode(
        `${driverSummary}${skippedSummary}${coDriverSummary}`,
        task,
        translate,
      )
    }

    return withGenerationMode(
      `${driverSummary} ${translate('businessTrip.generation-result-none')}`,
      task,
      translate,
    )
  } catch {
    return withGenerationMode(task.message || '', task, translate)
  }
}
